import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail } from "lucide-react";
import { toast } from "@/hooks/useToast";
import { useI18n } from '@/hooks/useI18n';
import { invokeEdgeFunction } from "@/integrations/supabase/functions";
import Loader from "@/components/ui/loader";

interface ResetPasswordFormProps {
  onBackToLogin: () => void;
}

const ResetPasswordForm = ({ onBackToLogin }: ResetPasswordFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { t } = useI18n();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: t('reset.emailRequiredTitle'),
        description: t('reset.emailRequiredDesc'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Chiama l'edge function invece di resetPasswordForEmail direttamente:
      // imposta approval_status = 'reset-psw' e invia il link via Mailgun.
      const { error } = await invokeEdgeFunction('request-password-reset', { email }, { useUserToken: false });
      
      if (error) {
        let errorMessage = t('reset.sendEmailError');
        if (typeof error === 'object' && (error as any)?.message?.includes("Too many requests")) {
          errorMessage = t('reset.tooManyRequests');
        }
        toast({
          title: t('reset.sendEmailErrorTitle'),
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        setIsEmailSent(true);
        toast({
          title: t('reset.emailSentTitle'),
          description: t('reset.emailSentDesc', { email }),
        });
      }
    } catch (err) {
      toast({
        title: t('reset.sendEmailErrorTitle'),
        description: t('reset.unexpected'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-gray-900 mb-2">
            {t('reset.emailSentTitle')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('reset.emailSentDesc', { email })}
          </p>
          <p className="text-gray-500">
            {t('reset.emailSentNote')}
          </p>
        </div>
        
        <div className="space-y-3">
          <Button 
            onClick={() => {
              setIsEmailSent(false);
              setEmail('');
            }}
            variant="outline"
            className="w-full border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            {t('reset.sendAgain')}
          </Button>
          
          <Button 
            onClick={onBackToLogin}
            variant="ghost"
            className="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('reset.backToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-gray-900 mb-2">
          {t('reset.title')}
        </h2>
        <p className="text-gray-600">
          {t('reset.requestDesc')}
        </p>
      </div>

      <form onSubmit={handleResetPassword} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="reset-email" className="font-medium text-gray-700">
            {t('auth.email')}
          </Label>
          <Input 
            id="reset-email" 
            type="email" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
            placeholder={t('login.emailPlaceholder')}
            required 
            disabled={isLoading}
          />
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium transition-colors duration-200"
          disabled={isLoading}
        >
            {isLoading ? (
              <>
                <Loader size="sm" className="mr-3" />
                {t('reset.sending')}
              </>
            ) : (
              <>
                <Mail className="w-5 h-5 mr-2" />
                {t('reset.sendResetLinkButton')}
              </>
            )}
        </Button>
      </form>

      <div className="text-center">
        <Button 
          onClick={onBackToLogin}
          variant="ghost"
          className="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('reset.backToLogin')}
        </Button>
      </div>
    </div>
  );
};

export default ResetPasswordForm; 